import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/85 hover:shadow-md hover:shadow-primary/30 active:bg-primary/80 active:shadow-xs',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/40',
        outline:
          'border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        // Calm tinted action that fills on hover; for several equal
        // choices side by side, where solid primaries would compete.
        soft: 'border border-accent/25 bg-accent/10 text-accent-strong hover:border-accent-strong hover:bg-accent-strong hover:text-accent-strong-foreground hover:shadow-md hover:shadow-accent-strong/25 active:shadow-xs',
        'soft-warning':
          'border border-warning/30 bg-warning/10 text-warning-strong hover:border-warning-strong hover:bg-warning-strong hover:text-warning-strong-foreground hover:shadow-md hover:shadow-warning-strong/25 active:shadow-xs',
        'soft-primary':
          'border border-primary/25 bg-primary/10 text-primary-strong hover:border-primary-strong hover:bg-primary-strong hover:text-primary-strong-foreground hover:shadow-md hover:shadow-primary-strong/25 active:shadow-xs',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
